<template>
  <header class="portal-header">
    <div class="portal-header__inner">
      <div class="portal-header__brand">
        <img
          class="portal-header__logo"
          :src="wordmarkSrc"
          alt="STRATACACHE"
        />
      </div>

      <div class="portal-header__divider portal-header__divider--brand" aria-hidden="true" />

      <div class="portal-header__title-block">
        <div class="portal-header__title">{{ $t("header.portalTitle") }}</div>
        <div class="portal-header__subtitle">{{ $t("header.subtitle") }}</div>
      </div>

      <div class="portal-header__spacer" aria-hidden="true" />

      <div v-if="isLoggedIn" class="portal-header__toolbar">
        <div class="portal-header__segment portal-header__status">
          <div class="portal-header__status-main">
            <span class="portal-header__live-dot" aria-hidden="true" />
            <span class="portal-header__live-label">{{ $t("header.live") }}</span>
          </div>
          <div class="portal-header__status-sync">
            <span class="portal-header__meta-secondary">
              {{ $t("header.lastSync") }}: {{ lastSyncDisplay }}
            </span>
            <button
              type="button"
              class="portal-header__icon-btn"
              :class="{ 'portal-header__icon-btn--spinning': syncRefreshing }"
              :title="$t('header.refresh')"
              :aria-label="$t('header.refresh')"
              :disabled="syncRefreshing"
              @click="handleSyncRefresh"
            >
              <span class="mdi mdi-refresh" aria-hidden="true" />
            </button>
          </div>
        </div>

        <div class="portal-header__divider" aria-hidden="true" />

        <div class="portal-header__segment portal-header__date">
          <span class="mdi mdi-calendar portal-header__segment-icon" aria-hidden="true" />
          <div class="portal-header__segment-text">
            <span class="portal-header__meta-primary">{{ dateLine }}</span>
            <span class="portal-header__meta-secondary">{{ weekdayLine }}</span>
          </div>
        </div>

        <div class="portal-header__divider" aria-hidden="true" />

        <div class="portal-header__segment portal-header__time">
          <span class="mdi mdi-clock-outline portal-header__segment-icon" aria-hidden="true" />
          <div class="portal-header__segment-text">
            <span class="portal-header__meta-primary">{{ timeLine }}</span>
            <span class="portal-header__meta-secondary">{{ $t('header.localTime') }} ({{ timezoneLabel }})</span>
          </div>
        </div>

        <div class="portal-header__divider" aria-hidden="true" />

        <div class="portal-header__segment portal-header__user">
          <b-dropdown
            right
            no-caret
            variant="link"
            toggle-class="portal-header__user-toggle"
            menu-class="portal-header__user-menu"
          >
            <template #button-content>
              <span class="mdi mdi-account-circle portal-header__user-icon" aria-hidden="true" />
              <div class="portal-header__segment-text portal-header__user-text">
                <span class="portal-header__meta-primary">{{ username }}</span>
                <span class="portal-header__meta-secondary">{{ userRole }}</span>
              </div>
              <span class="mdi mdi-chevron-down portal-header__user-chevron" aria-hidden="true" />
            </template>
            <b-dropdown-item
              v-if="permissions.includes('View-Audit')"
              @click="report"
            >
              Audit Reports
            </b-dropdown-item>
            <b-dropdown-item @click="profile">Profile Settings</b-dropdown-item>
            <b-dropdown-item @click="logout">{{ $t("nav.logout") }}</b-dropdown-item>
          </b-dropdown>
        </div>
      </div>

      <template v-else>
        <div class="portal-header__segment portal-header__date">
          <span class="mdi mdi-calendar portal-header__segment-icon" aria-hidden="true" />
          <div class="portal-header__segment-text">
            <span class="portal-header__meta-primary">{{ dateLine }}</span>
            <span class="portal-header__meta-secondary">{{ weekdayLine }}</span>
          </div>
        </div>

        <div class="portal-header__divider" aria-hidden="true" />

        <div class="portal-header__segment portal-header__time">
          <span class="mdi mdi-clock-outline portal-header__segment-icon" aria-hidden="true" />
          <div class="portal-header__segment-text">
            <span class="portal-header__meta-primary">{{ timeLine }}</span>
            <span class="portal-header__meta-secondary">{{ $t('header.localTime') }} ({{ timezoneLabel }})</span>
          </div>
        </div>

        <div class="portal-header__divider" aria-hidden="true" />

        <div class="portal-header__segment portal-header__auth">
          <div
            v-if="isAuthenticated == null"
            class="portal-header__auth-link"
            :title="'Login'"
            @click="goToRoute(routeNames.login)"
          >
            {{ $t("nav.login") }}
          </div>
          <div
            v-if="isAuthenticated == null && isSuperAdminConfigured == 'noadmin'"
            class="portal-header__auth-link"
            :title="'Register'"
            @click="goToRoute(routeNames.register)"
          >
            {{ $t("nav.register") }}
          </div>
          <div v-if="isAuthenticated == null" class="locale-switcher-wrap">
            <LocaleSwitcher />
          </div>
        </div>
      </template>
    </div>
  </header>
</template>

<script>
import axios from 'axios';
import moment from 'moment';
import LocaleSwitcher from './LocaleSwitcher.vue';
import { routeNames } from '../js/routeNames';
import { decryptData } from '../js/utils/encrypt';

export default {
  name: 'Header',
  components: {
    LocaleSwitcher
  },
  data() {
    return {
      isAuthenticated: '',
      username: '',
      userRole: '',
      permissions: [],
      isSuperAdminConfigured: '',
      wordmarkSrc: require('@/assets/STRATACACHE logo - White.svg'),
      now: moment(),
      lastSync: moment(),
      clockTimer: null,
      syncRefreshing: false,
    };
  },
  mounted() {
    this.isSuperAdminConfigured = localStorage.getItem('isSuperAdminConfigured');
    this.refreshAuthState();
    this.tickClock();
    this.clockTimer = setInterval(this.tickClock, 1000);
  },
  watch: {
    $route() {
      this.refreshAuthState();
    },
  },
  beforeDestroy() {
    if (this.clockTimer) {
      clearInterval(this.clockTimer);
    }
  },
  computed: {
    routeNames: () => routeNames,
    isLoggedIn() {
      return this.isAuthenticated === true;
    },
    dateLine() {
      return this.now.format('DD MMM YYYY');
    },
    weekdayLine() {
      return this.now.format('dddd');
    },
    timeLine() {
      return this.now.format('HH:mm');
    },
    timezoneLabel() {
      return this.formatUtcOffsetLabel(this.now.utcOffset());
    },
    lastSyncDisplay() {
      return this.lastSync.format('HH:mm:ss');
    },
  },
  methods: {
    refreshAuthState() {
      const secretKey = process.env.KEY || 'myscecretkey';
      let mkLocalData = localStorage.getItem('Login');
      let permissionData = localStorage.getItem('Permissions');
      if (!mkLocalData) {
        mkLocalData = 'null';
      }
      if (!permissionData) {
        permissionData = 'null';
      }
      this.isAuthenticated = decryptData(mkLocalData, secretKey);
      this.permissions = decryptData(permissionData, secretKey) || [];
      if (this.isLoggedIn) {
        const storedUser = localStorage.getItem('username');
        this.username = storedUser ? storedUser.replace(/['"]+/g, '') : '';
        const storedRole = localStorage.getItem('Role');
        this.userRole = this.formatRoleLabel(decryptData(storedRole, secretKey));
      } else {
        this.username = '';
        this.userRole = '';
      }
    },
    formatRoleLabel(role) {
      if (role == null || role === '') {
        return '';
      }
      if (typeof role === 'string') {
        return role.replace(/['"]+/g, '');
      }
      if (typeof role === 'object') {
        return role.name || role.display_name || role.title || '';
      }
      return String(role);
    },
    formatUtcOffsetLabel(offsetMinutes) {
      const sign = offsetMinutes >= 0 ? '+' : '-';
      const abs = Math.abs(offsetMinutes);
      const hours = Math.floor(abs / 60);
      const mins = abs % 60;
      if (mins === 0) {
        return `UTC${sign}${hours}`;
      }
      if (mins === 30) {
        return `UTC${sign}${hours}.5`;
      }
      return `UTC${sign}${hours}:${String(mins).padStart(2, '0')}`;
    },
    tickClock() {
      this.now = moment();
    },
    handleSyncRefresh() {
      if (this.syncRefreshing) {
        return;
      }
      this.syncRefreshing = true;
      this.lastSync = moment();
      this.$root.$emit('portal-header-refresh');
      window.setTimeout(() => {
        this.syncRefreshing = false;
      }, 600);
    },
    logout() {
      localStorage.removeItem('Login');
      if (routeNames.login === this.$router.currentRoute.name) {
        return;
      }
      axios
        .post(this.$hostName + '/logout', null)
        .then(() => {
          this.$router.push({ name: routeNames.login });
        })
        .catch((error) => {
          this.errors = error.response.data.errors;
        })
        .finally(() => {
          localStorage.removeItem('token');
          localStorage.removeItem('Permissions');
          localStorage.removeItem('username');
          localStorage.removeItem('Role');
          localStorage.removeItem('Airline');
          location.reload();
        });
    },
    profile() {
      if (this.$router.currentRoute.path.includes('/userprofile')) {
        return;
      }
      this.$router.push({ name: routeNames.userProfile });
    },
    report() {
      if (this.$router.currentRoute.path.includes('/report')) {
        return;
      }
      this.$router.push({ name: routeNames.report });
    },
    goToRoute(route) {
      if (route === this.$router.currentRoute.name) return;
      this.$router.push({ name: route });
    },
  },
};
</script>

<style scoped lang="scss">
$header-muted: rgba($brand-primary, 0.72);
$header-border: rgba($brand-primary, 0.12);
$header-live: #22a06b;

.portal-header {
  width: 100%;
  min-height: 72px;
  background-color: $general-white;
  border-bottom: 1px solid $header-border;
  position: relative;
  z-index: 100;

  &__inner {
    display: flex;
    align-items: center;
    gap: 0;
    width: 100%;
    height: 72px;
    padding: 0 16px 0 20px;
    min-width: 0;
    max-width: 100%;
    box-sizing: border-box;
  }

  &__toolbar {
    display: flex;
    align-items: center;
    flex: 0 0 auto;
    flex-shrink: 0;
    margin-left: auto;
    min-width: auto;
  }

  &__brand {
    flex-shrink: 0;
    padding-right: 0;
    display: flex;
    align-items: center;
  }

  &__logo {
    display: block;
    height: 40px;
    width: auto;
    max-width: 220px;
    object-fit: contain;
  }

  &__divider--brand {
    margin: 12px 16px 12px 12px;
  }

  &__title-block {
    flex: 1 1 auto;
    flex-shrink: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding-right: 12px;
    padding-left: 12px;
    min-height: 44px;
  }

  &__title {
    font-size: 17px;
    font-weight: 700;
    color: $brand-primary;
    line-height: 1.2;
    letter-spacing: -0.01em;
  }

  &__subtitle {
    margin-top: 2px;
    font-size: 12px;
    font-weight: 500;
    color: rgba($brand-primary, 0.78);
    line-height: 1.25;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__spacer {
    flex: 0 1 24px;
    min-width: 12px;
    max-width: 48px;
  }

  &__divider {
    width: 1px;
    align-self: stretch;
    margin: 12px 0;
    background: $header-border;
    flex-shrink: 0;
  }

  &__segment {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 0 12px;
    flex-shrink: 0;
    min-height: 44px;
  }

  &__date,
  &__time {
    flex-shrink: 0;

    .portal-header__segment-text {
      flex-shrink: 0;
      min-width: auto;
    }

    .portal-header__meta-primary,
    .portal-header__meta-secondary {
      max-width: none;
      overflow: visible;
      text-overflow: clip;
    }
  }

  &__segment-icon {
    font-size: 22px;
    color: $brand-primary;
    line-height: 1;
    flex-shrink: 0;
  }

  &__segment-text {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 1px;
    min-width: 0;
  }

  &__meta-primary {
    font-size: 14px;
    font-weight: 700;
    color: $brand-primary;
    line-height: 1.2;
    white-space: nowrap;
  }

  &__meta-secondary {
    font-size: 11px;
    font-weight: 500;
    color: $header-muted;
    line-height: 1.25;
    white-space: nowrap;
  }

  &__status {
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    gap: 2px;
    padding-top: 4px;
    padding-bottom: 4px;
    flex-shrink: 0;
  }

  &__status-main {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  &__live-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: $header-live;
    flex-shrink: 0;
  }

  &__live-label {
    font-size: 13px;
    font-weight: 700;
    color: $header-live;
    letter-spacing: 0.04em;
    line-height: 1;
  }

  &__status-sync {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  &__icon-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    padding: 0;
    border: none;
    background: transparent;
    color: $brand-primary;
    cursor: pointer;
    border-radius: 4px;
    -webkit-tap-highlight-color: transparent;

    .mdi {
      font-size: 18px;
    }

    &:hover:not(:disabled) {
      background: rgba($brand-primary, 0.06);
    }

    &:focus {
      outline: none;
    }

    &:active:not(:disabled),
    &:focus:not(:hover):not(:disabled) {
      background: transparent;
    }

    &:disabled {
      cursor: default;
      opacity: 0.7;
    }

    &--spinning .mdi {
      animation: portal-header-spin 0.7s linear infinite;
    }
  }

  @keyframes portal-header-spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  &__user {
    padding-right: 8px;
    flex-shrink: 0;
  }

  &__user-icon {
    font-size: 32px;
    color: $brand-primary;
    line-height: 1;
    flex-shrink: 0;
  }

  &__user-chevron {
    font-size: 18px;
    color: $brand-primary;
    margin-left: 2px;
    flex-shrink: 0;
  }

  &__auth {
    gap: 12px;
  }

  &__auth-link {
    font-size: 14px;
    font-weight: 600;
    color: $brand-primary;
    cursor: pointer;
    padding: 6px 10px;
    border-radius: 6px;

    &:hover {
      background: rgba($brand-primary, 0.06);
    }
  }
}

::v-deep .portal-header__user-toggle {
  display: flex !important;
  align-items: center;
  gap: 10px;
  padding: 0 !important;
  border: none !important;
  background: transparent !important;
  box-shadow: none !important;
  text-decoration: none !important;
  color: inherit !important;

  &:hover,
  &:focus,
  &:active {
    background: transparent !important;
    box-shadow: none !important;
    color: inherit !important;
  }
}

::v-deep .portal-header__user-menu {
  margin-top: 6px;
  border: 1px solid $header-border;
  box-shadow: 0 4px 16px rgba(24, 40, 72, 0.12);
}

.locale-switcher-wrap {
  flex-shrink: 0;
}

@media screen and (max-width: 1200px) {
  .portal-header {
    &__segment {
      padding: 0 12px;
    }

    &__logo {
      height: 34px;
      max-width: 180px;
    }

    &__title {
      font-size: 15px;
    }
  }
}

@media screen and (max-width: 992px) {
  .portal-header {
    min-height: auto;

    &__inner {
      flex-wrap: wrap;
      height: auto;
      min-height: 72px;
      padding: 10px 12px;
      row-gap: 8px;
    }

    &__spacer {
      display: none;
    }

    &__title-block {
      flex: 1 1 auto;
      min-width: 140px;
    }

    &__divider--brand {
      display: none;
    }

    &__toolbar {
      flex: 1 1 100%;
      margin-left: 0;
      flex-wrap: wrap;
      justify-content: flex-end;
      row-gap: 6px;
    }

    &__status,
    &__date,
    &__time,
    &__user,
    &__auth {
      padding: 4px 8px;
    }

    &__divider {
      display: none;
    }

    &__brand {
      padding-right: 12px;
    }
  }
}

@media screen and (max-width: 640px) {
  .portal-header {
    &__subtitle,
    &__status-sync .portal-header__meta-secondary {
      display: none;
    }

    &__logo {
      height: 28px;
      max-width: 140px;
    }

    &__segment-icon {
      font-size: 18px;
    }

    &__user-text .portal-header__meta-secondary {
      max-width: 100px;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
}
</style>
